import { useEffect, useRef, useState } from 'react';
import { FadeUp } from '../primitives/FadeUp';
import { useIsMobile } from '../../hooks/useIsMobile';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { ACCENT, BORDER, FONT_ACCENT, MUTED, TEXT_PRIMARY } from '../../tokens';

interface Stat {
  target: number;
  unit: string;
  label: string;
  sub: string;
}

const STATS: readonly Stat[] = [
  {
    target: 43,
    unit: ' pieces',
    label: 'Published every month, across every channel',
    sub: 'Five LinkedIn posts a week and five on Instagram and Facebook, carousels plus footage from the work you are already doing. Written and human-checked before anything goes live.',
  },
  {
    target: 2,
    unit: ' hours',
    label: 'Of your week, and only the part that needs you',
    sub: 'Review, approval, and a few phone clips from the job. Drafting, scheduling, resizing, review requests and reporting are already done before you open the laptop.',
  },
];

/**
 * Headline stats that count up the first time the section scrolls into view.
 *
 * Each figure runs on its own `requestAnimationFrame` loop with a staggered
 * duration, so they do not all land on the same frame.
 */
export function StatsSection() {
  const reduced = usePrefersReducedMotion();
  const isMobile = useIsMobile();
  const [values, setValues] = useState<readonly number[]>(() => STATS.map(() => 0));
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (reduced) {
      setValues(STATS.map((s) => s.target));
      return;
    }

    const el = ref.current;
    if (!el) return;

    const frames: number[] = [];

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();

        STATS.forEach((stat, idx) => {
          const duration = 1400 + idx * 200;
          const start = performance.now();

          const tick = (now: number): void => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);

            setValues((prev) => {
              const next = [...prev];
              next[idx] = Math.round(stat.target * eased);
              return next;
            });

            if (progress < 1) frames[idx] = requestAnimationFrame(tick);
          };

          frames[idx] = requestAnimationFrame(tick);
        });
      },
      { threshold: 0.4 },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      frames.forEach(cancelAnimationFrame);
    };
  }, [reduced]);

  return (
    <section
      ref={ref}
      style={{
        padding: isMobile ? '60px 20px' : '80px 64px',
        background: 'rgba(6,6,6,0.88)',
        borderTop: `1px solid ${BORDER}`,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? 48 : 80,
          alignItems: 'center',
        }}
      >
        {STATS.map((stat, i) => (
          <FadeUp key={stat.label} delay={i * 120}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 28 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 10 }}>
                  <span
                    style={{
                      fontFamily: FONT_ACCENT,
                      fontSize: 'clamp(4rem,7vw,6rem)',
                      fontWeight: 400,
                      fontStyle: 'italic',
                      color: TEXT_PRIMARY,
                      lineHeight: 1,
                    }}
                  >
                    {values[i] ?? 0}
                  </span>
                  <span
                    style={{
                      fontFamily: FONT_ACCENT,
                      fontSize: 'clamp(2rem,3.5vw,3.5rem)',
                      fontWeight: 400,
                      fontStyle: 'italic',
                      color: ACCENT,
                      lineHeight: 1,
                    }}
                  >
                    {stat.unit}
                  </span>
                </div>
                <div style={{ width: 40, height: 2, background: ACCENT, borderRadius: 2, marginBottom: 14 }} />
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: TEXT_PRIMARY, marginBottom: 6 }}>
                  {stat.label}
                </div>
                <div style={{ fontSize: '0.82rem', color: MUTED }}>{stat.sub}</div>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}
